import React, { type ChangeEvent, useCallback, useState } from "react";
import { Flex, Label, RadioButton, RadioGroup, LoadingIndicator } from "@jsq/ds/core";

import { TwoFaType } from "common/types/mainEnums";
import { SetupCard } from "../components/SetupCard";
import { type UnauthenticatedArenaObjectType } from "../../../types";

type Props = {
  onSwitch2FAMethodButtonClick?: () => void;
  showTotp2FAMethod?: () => void;
  showSMS2FAMethod?: () => void;
  showEmail2FAMethod?: () => void;
  onSubmit?: () => void;
  arena?: UnauthenticatedArenaObjectType;
  isSplitScreen?: boolean;
  isLoading?: boolean;
};

/**
 * A component for setting up TOTP MFA with an authenticator app.
 */
export const TwoFaSelection = ({
  onSwitch2FAMethodButtonClick,
  showTotp2FAMethod,
  showSMS2FAMethod,
  showEmail2FAMethod,
  onSubmit,
  arena,
  isSplitScreen,
  isLoading,
}: Props) => {
  const [selectedMethod, setSelectedMethod] = useState<string>("");

  const handleRadioChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSelectedMethod(event.target.value);
  }, []);

  const handleSubmit = useCallback(() => {
    if (selectedMethod as TwoFaType) {
      switch (selectedMethod) {
        case TwoFaType.Totp:
          showTotp2FAMethod?.();
          onSubmit?.();
          break;
        case TwoFaType.Sms:
          showSMS2FAMethod?.();
          onSubmit?.();
          break;
        case TwoFaType.Email:
          showEmail2FAMethod?.();
          onSubmit?.();
          break;
        default:
          break;
      }
    }
  }, [selectedMethod, showEmail2FAMethod, showSMS2FAMethod, showTotp2FAMethod, onSubmit]);

  return (
    <SetupCard
      onSwitch2FAMethodButtonClick={onSwitch2FAMethodButtonClick}
      methodTitle="Choose a two-factor authentication method"
      methodSubtitle="This is the method that you will use to verify your identity when logging in."
      submitButtonText="Continue"
      onSubmit={handleSubmit}
      // @ts-expect-error code written before better codegen types
      submitButtonBackgroundColor={arena?.signInButtonBackgroundColor}
      // @ts-expect-error code written before better codegen types
      submitButtonTextColor={arena?.signInButtonTextColor}
      isSplitScreen={isSplitScreen}
      isSubmitButtonDisabled={!selectedMethod}
    >
      <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={12}>
        <Label className="stacked-sm">Choose one of the following:</Label>
        {isLoading ? (
          <LoadingIndicator variant="primary" />
        ) : (
          <RadioGroup
            name="twoFaMethodSelection"
            className="stacked-md"
            value={selectedMethod}
            onChange={handleRadioChange}
          >
            {showTotp2FAMethod && (
              <RadioButton
                data-testid="totp-radio-button"
                data-cy="totp-radio-button"
                value={TwoFaType.Totp}
              >
                Authenticator app
              </RadioButton>
            )}
            {showSMS2FAMethod && (
              <RadioButton
                data-testid="sms-radio-button"
                data-cy="sms-radio-button"
                value={TwoFaType.Sms}
              >
                Text message
              </RadioButton>
            )}
            {showEmail2FAMethod && (
              <RadioButton
                data-testid="email-radio-button"
                data-cy="email-radio-button"
                value={TwoFaType.Email}
              >
                Email
              </RadioButton>
            )}
          </RadioGroup>
        )}
      </Flex>
    </SetupCard>
  );
};

export default TwoFaSelection;
